import { clsx } from 'clsx';

import {
  BALLOON_HOVERED_Z_INDEX,
  BALLOON_OFFSET_TOP,
  BALLOON_Z_INDEX,
} from '@/microfrontends/map/shared/constants/map';
import { PinService } from '@/microfrontends/map/shared/services/PinService';

import { type Cluster } from '@/packages/query-state/shared/types/clusters/ClustersResponse';

import { PIN_EXTENDED_TYPES } from '../PinsLayer/types';

import { type BalloonFeature } from './types';

import styles from './styles.css';

export function defineBalloonManager(ymaps: YMaps.IYMaps): void {
  if (ymaps.modules.isDefined('BalloonManager')) {
    return;
  }

  ymaps.modules.define<
    [YMaps.ITemplateLayoutFactory, typeof YMaps.shape.Rectangle, typeof YMaps.geometry.pixel.Rectangle]
  >(
    'BalloonManager',
    ['templateLayoutFactory', 'shape.Rectangle', 'geometry.pixel.Rectangle'],
    (
      provide,
      templateLayoutFactory: YMaps.ITemplateLayoutFactory,
      shapeRectangle: typeof YMaps.shape.Rectangle,
      geometryPixelRectangle: typeof YMaps.geometry.pixel.Rectangle,
    ) => {
      class BalloonManager {
        public readonly objectManager: YMaps.ObjectManager;

        public constructor() {
          this.objectManager = new ymaps.ObjectManager();
        }

        public removeBalloons = (): void => {
          this.objectManager.removeAll();
        };

        public removeBalloon = (clusterId: string): void => {
          const object = this.objectManager.objects.getById<Cluster>(clusterId);

          if (object) {
            this.objectManager.remove([object.id]);
          }
        };

        public updateBalloon = (cluster: Cluster): void => {
          const balloonFeature = this.prepareBalloonFeature(cluster);

          if (!this.isBalloonFeature(balloonFeature)) {
            return;
          }

          this.objectManager.add({ type: 'FeatureCollection', features: [balloonFeature] });

          this.updateBalloonOptions(cluster);
        };

        public updateBalloons = (clusters: Cluster[]): void => {
          this.objectManager.removeAll();

          const features = clusters.map(cluster => this.prepareBalloonFeature(cluster)).filter(this.isBalloonFeature);

          this.objectManager.add({ features, type: 'FeatureCollection' });

          /**
           * Это хак, который позволяет динамически обновлять UI баллунов при изменении кластеров.
           * Простым "removeAll()"" и "add()"" это не решается.
           * Возможно это можно сделать иначе, но я пока не придумал как.
           */
          clusters.forEach(this.updateBalloonOptions);
        };

        public updateBalloonOptions = (cluster: Cluster): void => {
          const pinService = this.getPinService(cluster);
          const pinExtendedTypes = pinService.provideExtendedTypes();

          const pinWithBalloon = pinExtendedTypes.has(PIN_EXTENDED_TYPES.withBalloon);

          if (!pinWithBalloon) {
            return;
          }

          this.objectManager.objects.setObjectOptions(cluster.id, {
            iconLayout: this.prepareBalloonLayout(pinService),
            zIndex: this.getBalloonZIndex(pinService),
          });
        };

        public updateBalloonProperties = (cluster: Cluster): void => {
          const pinService = this.getPinService(cluster);
          const pinExtendedTypes = pinService.provideExtendedTypes();

          const pinWithBalloon = pinExtendedTypes.has(PIN_EXTENDED_TYPES.withBalloon);

          if (!pinWithBalloon) {
            return;
          }

          const objectManagerCluster = this.objectManager.objects.getById<Cluster>(cluster.id);

          if (objectManagerCluster) {
            objectManagerCluster.properties = cluster.properties;
          }
        };

        private isBalloonFeature = (feature: BalloonFeature | null): feature is BalloonFeature => !!feature;

        private getBalloonZIndex = (pinService: PinService): number => {
          const pinExtendedTypes = pinService.provideExtendedTypes();
          const pinActive = pinExtendedTypes.has(PIN_EXTENDED_TYPES.active);
          const pinHovered = pinExtendedTypes.has(PIN_EXTENDED_TYPES.hovered);

          return pinActive || pinHovered ? BALLOON_HOVERED_Z_INDEX : BALLOON_Z_INDEX;
        };

        private prepareBalloonFeature = (cluster: Cluster): BalloonFeature | null => {
          const pinService = this.getPinService(cluster);
          const pinExtendedTypes = pinService.provideExtendedTypes();

          const pinWithBalloon = pinExtendedTypes.has(PIN_EXTENDED_TYPES.withBalloon);

          if (!pinWithBalloon) {
            return null;
          }

          return {
            ...cluster,
            options: {
              iconLayout: this.prepareBalloonLayout(pinService),
              zIndex: this.getBalloonZIndex(pinService),
            },
          };
        };

        private prepareBalloonLayout = (pinService: PinService): Function => {
          const content = this.prepareBalloonContent(pinService);

          /**
           * Чтобы балун поддерживал автопозиционирование, то есть автоматически подстраивался под размер содержимого,
           * необходимо правильно оповещать о его текущих размерах. Для этого необходимо реализовать метод getShape().
           */
          return templateLayoutFactory.createClass(content, {
            /**
             * Это запись означает, что мы переопределяем метод "getShape" у layout.templateBased.Base,
             * от которого наследуется класс, созданный при помощи "templateLayoutFactory.createClass()"
             */
            getShape() {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const element = (this as any).getElement() as HTMLElement; // magic
              const wrapperElement = element?.firstElementChild;

              if (!wrapperElement) {
                return null;
              }

              const boundingRect = wrapperElement.getBoundingClientRect();
              const offsetX = -boundingRect.width / 2;
              const offsetY = -boundingRect.height - BALLOON_OFFSET_TOP;

              return new shapeRectangle(
                new geometryPixelRectangle([
                  [offsetX, offsetY],
                  [offsetX + boundingRect.width, offsetY + boundingRect.height],
                ]),
              );
            },
          });
        };

        private prepareBalloonContent = (pinService: PinService): string => {
          const pinExtendedTypes = pinService.provideExtendedTypes();

          const pinWithCount = pinExtendedTypes.has(PIN_EXTENDED_TYPES.withCount);
          const promotedPin = pinExtendedTypes.has(PIN_EXTENDED_TYPES.glow);
          const pinWithBalloon = pinExtendedTypes.has(PIN_EXTENDED_TYPES.withBalloon);
          const pinInFavorite = pinExtendedTypes.has(PIN_EXTENDED_TYPES.favorite);
          const pinViewed = pinExtendedTypes.has(PIN_EXTENDED_TYPES.viewed);
          const pinActive = pinExtendedTypes.has(PIN_EXTENDED_TYPES.active);
          const pinHovered = pinExtendedTypes.has(PIN_EXTENDED_TYPES.hovered);

          const count = pinService.provideCount();
          const title = pinService.provideTitle();
          const details = pinService.provideDetails();
          const label = pinService.provideLabel();

          const countClassName = clsx(styles['count'], promotedPin && !pinInFavorite && styles['count--promoted']);

          const countMarkup = pinWithCount ? `<div class="${countClassName}">${count}</div>` : '';
          const favoriteMarkup = pinInFavorite ? `<div class="${styles['favorite']}"></div>` : '';
          const titleMarkup = pinWithBalloon ? `<div class="${styles['title']}">${title}</div>` : '';
          const detailsMarkup =
            pinWithBalloon && details
              ? `<div class="${clsx(styles['details'], { [styles['counter']]: pinWithCount })}">${details}</div>`
              : '';
          const labelMarkup = pinWithBalloon && label ? `<div class="${styles['label']}">${label}</div>` : '';

          const balloonClassName = clsx(styles['balloon'], pinActive && styles['active']);

          return `
            <div class="${clsx(styles['wrapper'], {
              [styles['viewed']]: pinViewed,
              [styles['with-details']]: details,
              [styles['hovered']]: pinHovered,
            })}">
              <div class="${balloonClassName}">
                ${labelMarkup}
                ${countMarkup}
                <div class="${styles['content']}">
                  ${titleMarkup}
                  ${detailsMarkup}
                </div>
                ${favoriteMarkup}
              </div>
            </div>
          `;
        };

        private getPinService = (cluster: Cluster): PinService => {
          return new PinService({ cluster });
        };
      }

      provide(BalloonManager);
    },
  );
}
